import * as THREE from "three";
import type { BallSnap, LobbyState, PlayerSnap } from "@genjiball/protocol";
import { RemotePlayer } from "../entities/remotePlayer.js";
import { Ball } from "../entities/ball.js";
import { makeTargetAura, disposeAura } from "../render/targetAura.js";

export class MatchScene {
  readonly scene: THREE.Scene;
  private players: Map<string, RemotePlayer> = new Map();
  private ball: Ball;
  private targetId: string | null = null;
  private currentAura: THREE.Mesh | null = null;
  private localPlayerId: string | null = null;

  constructor() {
    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0x141a24);
    this.scene.fog = new THREE.Fog(0x141a24, 40, 120);

    new THREE.TextureLoader().load(
      "/2310-w062-n005-290B-p15-290.jpg",
      (tex) => {
        tex.mapping = THREE.EquirectangularReflectionMapping;
        tex.colorSpace = THREE.SRGBColorSpace;
        this.scene.background = tex;
        this.scene.environment = tex;
      },
    );

    this.scene.add(new THREE.HemisphereLight(0xbfd6ff, 0x2a1f15, 0.55));

    const sun = new THREE.DirectionalLight(0xfff1c8, 1.6);
    sun.position.set(18, 28, 12);
    sun.castShadow = true;
    sun.shadow.mapSize.set(2048, 2048);
    sun.shadow.camera.left = -22;
    sun.shadow.camera.right = 22;
    sun.shadow.camera.top = 22;
    sun.shadow.camera.bottom = -22;
    sun.shadow.camera.near = 1;
    sun.shadow.camera.far = 70;
    sun.shadow.bias = -0.0005;
    this.scene.add(sun);

    const rim = new THREE.DirectionalLight(0x88aaff, 0.5);
    rim.position.set(-15, 20, -12);
    this.scene.add(rim);

    const platform = new THREE.Mesh(
      new THREE.BoxGeometry(30, 30, 30),
      new THREE.MeshStandardMaterial({ color: 0x3d4855, roughness: 0.9 }),
    );
    platform.position.set(0, -15, 0);
    platform.receiveShadow = true;
    platform.castShadow = true;
    this.scene.add(platform);

    this.ball = new Ball();
    this.scene.add(this.ball.group);
  }

  setLocalPlayerId(id: string) {
    this.localPlayerId = id;
    const stale = this.players.get(id);
    if (stale) {
      this.scene.remove(stale.group);
      stale.dispose();
      this.players.delete(id);
    }
  }

  syncLobby(lobby: LobbyState) {
    for (const id of this.players.keys()) {
      if (!lobby.players[id]) {
        const p = this.players.get(id)!;
        this.scene.remove(p.group);
        p.dispose();
        this.players.delete(id);
      }
    }
    for (const lp of Object.values(lobby.players)) {
      if (lp.id === this.localPlayerId) continue;
      let p = this.players.get(lp.id);
      if (!p) {
        p = new RemotePlayer(lp.id, lp.name, lp.color, [0, 0, 0]);
        this.scene.add(p.group);
        this.players.set(lp.id, p);
      } else {
        p.applyMeta(lp.name, lp.color);
      }
    }
  }

  ingestSnapshot(serverTimeMs: number, players: PlayerSnap[], ball: BallSnap | null) {
    for (const s of players) {
      const p = this.players.get(s.id);
      if (!p) continue;
      p.ingestSnapshot(serverTimeMs, s.pos, s.yaw);
    }
    if (ball) {
      this.ball.ingest(serverTimeMs, ball.pos, ball.vel);
      this.setTarget(ball.targetId);
    }
  }

  setTarget(newTargetId: string | null) {
    if (newTargetId === this.targetId) return;
    if (this.currentAura) {
      this.currentAura.parent?.remove(this.currentAura);
      disposeAura(this.currentAura);
      this.currentAura = null;
    }
    this.targetId = newTargetId;
    if (newTargetId) {
      const p = this.players.get(newTargetId);
      if (p) {
        this.currentAura = makeTargetAura();
        p.group.add(this.currentAura);
      }
    }
    if (newTargetId) this.ball.resetTrail([0, 2, 0]);
  }

  update(renderTimeMs: number) {
    for (const p of this.players.values()) p.update(renderTimeMs);
    this.ball.update(renderTimeMs);
  }

  getPlayerGroup(id: string): THREE.Object3D | null {
    return this.players.get(id)?.group ?? null;
  }
}
